import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import PageToolbarFilters, { defaultGetOptionValue } from "../../components/Filters/PageToolbarFilters";
import ToolbarSearch from "../../components/Filters/ToolbarSearch";
import AddToListModal from "../../components/MyVendors/AddToListModal";
import CreateVendorListModal from "../../components/MyVendors/CreateVendorListModal";
import DeleteVendorListModal from "../../components/MyVendors/DeleteVendorListModal";
import RemoveVendorFromListModal from "../../components/MyVendors/RemoveVendorFromListModal";
import MyVendorsLibraryDrawer from "../../components/MyVendors/MyVendorsLibraryDrawer";
import MyVendorsSelectionBar from "../../components/MyVendors/MyVendorsSelectionBar";
import ManualSortToggle from "../../components/MyVendors/ManualSortToggle";
import VendorListViewGrid from "../../components/MyVendors/VendorListViewGrid";
import useMyVendors from "../../custom-hooks/useMyVendors";
import useMyVendorMembershipActions from "../../custom-hooks/useMyVendorMembershipActions";
import useMyVendorLists from "../../custom-hooks/useMyVendorLists";
import useMyVendorListActions from "../../custom-hooks/useMyVendorListActions";
import useVendorListTableEnrichment from "../../custom-hooks/useVendorListTableEnrichment";
import useBookmarkedVendors from "../../hooks/useBookmarkedVendors";
import useVendorTags from "../../hooks/useVendorTags";
import { useSystemListMemberOrderQuery } from "../../custom-hooks/queries";
import { useDecodedToken } from "../../custom-hooks/useToken";
import useVendorListDetailStickyFilters from "../../custom-hooks/useVendorListDetailStickyFilters";
import { setVendorsFilters } from "../../redux-store/features/indicationsDashboardFiltersSlice";
import { setDrawer } from "../../redux-store/features/layoutSlice";
import { setSearchTextByPage } from "../../redux-store/features/providerFiltersSlice";
import { canUseTrialExperienceDashboard } from "../../utils/orgFeatureGates";
import { resolveRequestAction, getRequestVqqCreatePath, getRequestVqaCreatePath } from "../../utils/myVendorsRequestUtils";
import { exportVendorListAsCsv, exportVendorListAsExcel } from "../../utils/exportVendorList";
import { vendorsFromListForCreate, isVendorListDetailLookupPending } from "../../utils/myVendorListsUtils";
import {
  MY_VENDORS_ROUTE,
  MY_VENDORS_TAB_LISTS,
} from "../../utils/myVendorsFilterOptions";
import {
  isSystemListId,
  getSystemListKind,
  SYSTEM_LIST_IDS,
  VENDOR_LIST_DETAIL_ROUTE,
} from "../../utils/myVendorListsConstants";
import { isManualOnlyVendor } from "../../utils/myVendorsConstants";
import {
  applyVendorListViewFilters,
  applyVendorListViewSearch,
  buildVendorListViewFilterSections,
  getDefaultManualSortOrder,
  getListPersistedSortOrder,
  getVendorNameAscendingSortOrder,
  mergeManualSortWithNewMembers,
  mergeVisibleReorder,
  resolveVendorListTableRows,
} from "../../utils/myVendorListViewUtils";

const PAGE_KEY = "vendorListDetail";

/** Border on wrapper — @tailwindcss/forms clears ring/outline on focused inputs. */
const LIST_INLINE_FIELD_WRAPPER_CLASS =
  "rounded-md border-2 border-gray-300 bg-white transition-colors focus-within:border-indigo-600";

/** Notes sit on gray-50 — transparent fill + lighter border (prototype ring-gray-200). */
const LIST_NOTES_FIELD_WRAPPER_CLASS =
  "rounded-md border-2 border-gray-200 bg-transparent transition-colors focus-within:border-indigo-600";

const LIST_INLINE_FIELD_CONTROL_CLASS =
  "block w-full rounded-md border-0 bg-transparent focus:outline-none focus:ring-0 focus:ring-offset-0";

export default function VendorListDetailPage() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedIds, setSelectedIds] = useVendorListDetailStickyFilters(listId);
  const [selectedVendorIds, setSelectedVendorIds] = useState(() => new Set());
  const [isManualSort, setIsManualSort] = useState(false);
  const [manualSortOrder, setManualSortOrder] = useState([]);
  const [defaultSortOrder, setDefaultSortOrder] = useState([]);
  const [notes, setNotes] = useState("");
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [createListModalOpen, setCreateListModalOpen] = useState(false);
  const [addToListModalOpen, setAddToListModalOpen] = useState(false);
  const [addToListTargetVendors, setAddToListTargetVendors] = useState([]);
  const [vendorPendingRemove, setVendorPendingRemove] = useState(null);
  const [vendorsPendingBulkRemove, setVendorsPendingBulkRemove] = useState([]);
  const [bulkRemoveSkippedCount, setBulkRemoveSkippedCount] = useState(0);
  const [isRemovingVendor, setIsRemovingVendor] = useState(false);
  const [libraryDrawerDocId, setLibraryDrawerDocId] = useState(null);
  const [pendingVendorActions, setPendingVendorActions] = useState({});
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  const nameInputRef = useRef(null);

  const searchQuery =
    useSelector((state) => state.providersFilters.searchTextByPage[PAGE_KEY]) || "";
  const loggedInUser = useSelector((state) => state.loggedInUser);
  const { rights } = useSelector((state) => state.userRights);
  const isDrawerOpen = useSelector((state) => state.layout.drawerOpen);

  const { decoded, error: tokenError } = useDecodedToken();
  const sponsorId = decoded?.sponsor_id ?? null;

  const { vendorTagsMap } = useVendorTags(sponsorId);
  const { vendors: myVendors } = useMyVendors(sponsorId, vendorTagsMap);
  const { bookmarkedProviderIds, isBookmarked, toggleBookmark } = useBookmarkedVendors(sponsorId);
  const { lists: vendorLists, userLists: userVendorLists, isLoading: listsLoading } =
    useMyVendorLists(sponsorId, {
    myVendors,
    bookmarkedProviderIds,
  });
  const listLookupPending = isVendorListDetailLookupPending({
    decoded,
    tokenError,
    listsLoading,
  });

  const {
    renameList,
    updateListNotes,
    deleteList,
    removeMemberFromList,
    reorderListMembers,
    reorderSystemListMembers,
    createList,
    addMembersToList,
    isMutating: listMutating,
  } = useMyVendorListActions(sponsorId, { myVendors, vendorTagsMap });

  const {
    handleRemoveFromMyVendors,
    handleAddToHidden,
    handleUnhide,
    isMutating: membershipMutating,
  } = useMyVendorMembershipActions(sponsorId);

  const canCompareTrialExperience = canUseTrialExperienceDashboard(loggedInUser, rights);
  const currentList = useMemo(
    () => vendorLists.find((list) => String(list.id) === String(listId)),
    [vendorLists, listId]
  );
  const isSystemList = Boolean(currentList && isSystemListId(currentList.id));
  const systemListKind = isSystemList ? getSystemListKind(listId) : null;
  const { data: systemListOrderData, isFetched: systemListOrderFetched } =
    useSystemListMemberOrderQuery(sponsorId, systemListKind, isSystemList);
  const storedSystemListOrder = useMemo(
    () => (systemListOrderData?.provider_ids || []).map(String),
    [systemListOrderData?.provider_ids]
  );
  const systemOrderReady = !isSystemList || systemListOrderFetched;

  const { enrichmentVendors } = useVendorListTableEnrichment(
    currentList,
    myVendors,
    vendorTagsMap
  );

  const listVendors = useMemo(() => {
    if (!currentList) return [];
    return resolveVendorListTableRows(
      currentList,
      [...myVendors, ...enrichmentVendors],
      vendorTagsMap
    )
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [currentList, myVendors, enrichmentVendors, vendorTagsMap]);

  const listVendorIdsKey = useMemo(
    () =>
      listVendors
        .map((vendor) => String(vendor.id))
        .sort()
        .join(","),
    [listVendors]
  );

  const listMemberSortKey = useMemo(
    () => listVendors.map((vendor) => `${vendor.id}:${vendor.sortOrder ?? ""}`).join("|"),
    [listVendors]
  );

  const storedSystemOrderKey = useMemo(
    () => storedSystemListOrder.join(","),
    [storedSystemListOrder]
  );

  useEffect(() => {
    setIsManualSort(false);
    setManualSortOrder([]);
    setDefaultSortOrder([]);
  }, [listId]);

  useEffect(() => {
    if (!listVendors.length) {
      setManualSortOrder([]);
      setDefaultSortOrder([]);
      return;
    }
    if (!systemOrderReady) return;

    setDefaultSortOrder(getVendorNameAscendingSortOrder(listVendors));

    setManualSortOrder((prev) => {
      if (isSystemList) {
        if (storedSystemListOrder.length > 0) {
          return mergeManualSortWithNewMembers(storedSystemListOrder, listVendors);
        }
        if (prev.length > 0) {
          return mergeManualSortWithNewMembers(prev, listVendors);
        }
        return getDefaultManualSortOrder(listVendors);
      }

      return mergeManualSortWithNewMembers(getListPersistedSortOrder(listVendors), listVendors);
    });
  }, [
    listId,
    listVendorIdsKey,
    listMemberSortKey,
    isSystemList,
    storedSystemOrderKey,
    systemOrderReady,
  ]);

  useEffect(() => {
    if (currentList) {
      setNotes(currentList.notes || "");
      setEditedName(currentList.name || "");
    }
  }, [currentList]);

  useEffect(() => {
    if (!isDrawerOpen) setLibraryDrawerDocId(null);
  }, [isDrawerOpen]);

  const filterSections = useMemo(
    () => buildVendorListViewFilterSections({ listVendors }),
    [listVendors]
  );

  const filteredVendors = useMemo(() => {
    const searched = applyVendorListViewSearch(listVendors, searchQuery);
    return applyVendorListViewFilters(searched, selectedIds);
  }, [listVendors, searchQuery, selectedIds]);

  const selectedVendors = useMemo(
    () => filteredVendors.filter((vendor) => selectedVendorIds.has(String(vendor.id))),
    [filteredVendors, selectedVendorIds]
  );

  const allFilteredSelected =
    filteredVendors.length > 0 &&
    filteredVendors.every((vendor) => selectedVendorIds.has(String(vendor.id)));
  const someFilteredSelected = filteredVendors.some((vendor) =>
    selectedVendorIds.has(String(vendor.id))
  );

  const setVendorActionPending = useCallback((providerId, key, value) => {
    setPendingVendorActions((prev) => ({
      ...prev,
      [String(providerId)]: { ...(prev[String(providerId)] || {}), [key]: value },
    }));
  }, []);

  const isVendorActionPending = useCallback(
    (providerId, key) => Boolean(pendingVendorActions[String(providerId)]?.[key]),
    [pendingVendorActions]
  );

  const handleToggleManualSort = useCallback(() => {
    setIsManualSort((prev) => !prev);
  }, []);

  const handleToggleVendorSelection = useCallback((vendor) => {
    const id = String(vendor.id);
    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAllFiltered = useCallback(() => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredVendors.map((vendor) => String(vendor.id)));
      setSelectedVendorIds((prev) => {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.delete(id));
        return next;
      });
      return;
    }
    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      filteredVendors.forEach((vendor) => next.add(String(vendor.id)));
      return next;
    });
  }, [allFilteredSelected, filteredVendors]);

  const handleReorderVendors = useCallback(
    async (visibleProviderIds) => {
      const fullBase = manualSortOrder.length
        ? manualSortOrder
        : getDefaultManualSortOrder(listVendors);
      const merged = mergeVisibleReorder(fullBase, visibleProviderIds);
      const previousOrder = manualSortOrder.length ? manualSortOrder : fullBase;

      setManualSortOrder(merged);
      setIsManualSort(true);

      try {
        if (isSystemList) {
          const kind = getSystemListKind(listId);
          if (!kind) return;
          await reorderSystemListMembers(kind, merged);
          return;
        }
        await reorderListMembers(listId, merged);
      } catch {
        setManualSortOrder(previousOrder);
      }
    },
    [
      isSystemList,
      listId,
      listVendors,
      manualSortOrder,
      reorderListMembers,
      reorderSystemListMembers,
    ]
  );

  const handleRemoveFromList = useCallback(
    async (vendor) => {
      const providerId = vendor?.id;
      if (!providerId || !currentList) return;

      if (isSystemList) {
        if (currentList.id === SYSTEM_LIST_IDS.BOOKMARKED) {
          await toggleBookmark(providerId);
        } else if (currentList.id === SYSTEM_LIST_IDS.MY_VENDORS && isManualOnlyVendor(vendor)) {
          await handleRemoveFromMyVendors(vendor);
        }
        setSelectedVendorIds((prev) => {
          const next = new Set(prev);
          next.delete(String(providerId));
          return next;
        });
        return;
      }

      await removeMemberFromList(listId, providerId);
      setSelectedVendorIds((prev) => {
        const next = new Set(prev);
        next.delete(String(providerId));
        return next;
      });
    },
    [currentList, handleRemoveFromMyVendors, isSystemList, listId, removeMemberFromList, toggleBookmark]
  );

  const canRemoveFromList = useCallback(
    (vendor) => {
      if (!isSystemList) return true;
      if (currentList?.id === SYSTEM_LIST_IDS.BOOKMARKED) return true;
      if (currentList?.id === SYSTEM_LIST_IDS.MY_VENDORS) return isManualOnlyVendor(vendor);
      return false;
    },
    [currentList?.id, isSystemList]
  );

  const handleRequestRemoveFromList = useCallback((vendor) => {
    if (!vendor?.id) return;
    setVendorsPendingBulkRemove([]);
    setBulkRemoveSkippedCount(0);
    setVendorPendingRemove(vendor);
  }, []);

  const handleRequestRemoveSelected = useCallback(() => {
    if (!selectedVendors.length) return;

    let targets = selectedVendors;
    let skippedCount = 0;

    if (currentList?.id === SYSTEM_LIST_IDS.MY_VENDORS) {
      targets = selectedVendors.filter((vendor) => isManualOnlyVendor(vendor));
      skippedCount = selectedVendors.length - targets.length;
      if (!targets.length) {
        window.alert(
          "Only manually added vendors can be removed from My Vendors. Hide platform vendors or remove bookmarks instead."
        );
        return;
      }
    }

    setVendorPendingRemove(null);
    setBulkRemoveSkippedCount(skippedCount);
    setVendorsPendingBulkRemove(targets);
  }, [currentList?.id, selectedVendors]);

  const handleConfirmRemoveFromList = useCallback(async () => {
    if (isRemovingVendor) return;

    const targets = vendorPendingRemove
      ? [vendorPendingRemove]
      : vendorsPendingBulkRemove;
    if (!targets.length) return;

    setIsRemovingVendor(true);
    try {
      await Promise.all(targets.map((vendor) => handleRemoveFromList(vendor)));
      setVendorPendingRemove(null);
      setVendorsPendingBulkRemove([]);
      setBulkRemoveSkippedCount(0);
      setSelectedVendorIds(new Set());
    } finally {
      setIsRemovingVendor(false);
    }
  }, [
    handleRemoveFromList,
    isRemovingVendor,
    vendorPendingRemove,
    vendorsPendingBulkRemove,
  ]);

  const handleCancelRemoveModal = useCallback(() => {
    setVendorPendingRemove(null);
    setVendorsPendingBulkRemove([]);
    setBulkRemoveSkippedCount(0);
  }, []);

  const handleOpenAddToListModal = useCallback((vendors = []) => {
    if (!vendors.length) return;
    setAddToListTargetVendors(vendors);
    setAddToListModalOpen(true);
  }, []);

  const handleVendorAction = useCallback(
    async (actionId, vendor) => {
      const providerId = vendor?.id;
      if (!providerId) return;
      const pendingKey =
        actionId === "toggle-bookmark"
          ? "bookmark"
          : actionId === "add-to-hidden"
            ? "hide"
            : actionId === "unhide" || actionId === "remove-from-hidden"
              ? "unhide"
              : null;
      try {
        if (pendingKey) setVendorActionPending(providerId, pendingKey, true);
        switch (actionId) {
          case "view-profile":
            navigate(`/provider/${providerId}/overview`);
            break;
          case "request-vqq": {
            const path = getRequestVqqCreatePath(providerId);
            if (path) navigate(path);
            break;
          }
          case "request-vqa": {
            const path = getRequestVqaCreatePath(providerId);
            if (path) navigate(path);
            break;
          }
          case "remove-from-my-vendors":
            await handleRemoveFromMyVendors(vendor);
            break;
          case "add-to-hidden":
            await handleAddToHidden(vendor);
            break;
          case "unhide":
          case "remove-from-hidden":
            await handleUnhide(vendor);
            break;
          case "toggle-bookmark":
            await toggleBookmark(providerId);
            break;
          default:
            break;
        }
      } finally {
        if (pendingKey) setVendorActionPending(providerId, pendingKey, false);
      }
    },
    [
      handleAddToHidden,
      handleRemoveFromMyVendors,
      handleUnhide,
      navigate,
      setVendorActionPending,
      toggleBookmark,
    ]
  );

  const handleRequestAction = useCallback(
    (request) => {
      const action = resolveRequestAction(request);
      if (!action) return;
      if (action.kind === "drawer") {
        setLibraryDrawerDocId(action.documentId);
        dispatch(setDrawer(true));
        return;
      }
      if (action.kind === "navigate") navigate(action.path);
    },
    [dispatch, navigate]
  );

  const handleSaveNotes = async () => {
    if (!currentList || isSystemList) {
      setIsEditingNotes(false);
      return;
    }
    await updateListNotes(listId, notes);
    setIsEditingNotes(false);
  };

  const handleSaveRename = async () => {
    if (isSystemList) {
      setIsRenaming(false);
      return;
    }
    const trimmed = editedName.trim();
    if (trimmed && trimmed !== currentList?.name) {
      await renameList(listId, trimmed);
    }
    setIsRenaming(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteList(listId);
    } catch (error) {
      console.error("Failed to delete vendor list:", error);
      setShowDeleteModal(false);
      return;
    }
    setShowDeleteModal(false);
    navigate(`${MY_VENDORS_ROUTE}?tab=${MY_VENDORS_TAB_LISTS}`, { replace: true });
  };

  const handleCompareSelected = () => {
    if (!selectedVendors.length || !canCompareTrialExperience) return;
    dispatch(
      setVendorsFilters({
        providerIds: selectedVendors.map((vendor) => vendor.id).filter(Boolean),
      })
    );
    navigate("/TrialExperience");
  };

  const handleCreateListFromSelection = async (payload) => {
    const created = await createList(payload);
    if (created?.id) {
      setCreateListModalOpen(false);
      navigate(`${VENDOR_LIST_DETAIL_ROUTE}/${created.id}`);
    }
  };

  const handleAddVendorsToList = async (list, vendors) => {
    await addMembersToList(list.id, vendors);
    setAddToListModalOpen(false);
    setAddToListTargetVendors([]);
  };

  const handleDuplicateList = useCallback(async () => {
    if (!currentList) return;
    const copy = await createList({
      name: `${currentList.name} (Copy)`,
      notes: currentList.notes || "",
      vendors: vendorsFromListForCreate(currentList),
    });
    if (copy?.id) navigate(`${VENDOR_LIST_DETAIL_ROUTE}/${copy.id}`);
  }, [createList, currentList, navigate]);

  const handleExportCsv = useCallback(() => {
    exportVendorListAsCsv(currentList, listVendors);
  }, [currentList, listVendors]);

  const handleExportExcel = useCallback(async () => {
    setIsExportingExcel(true);
    try {
      await exportVendorListAsExcel(currentList, listVendors);
    } catch (error) {
      console.error("Excel export failed:", error);
      window.alert(error?.message || "Could not export list to Excel. Please try again.");
    } finally {
      setIsExportingExcel(false);
    }
  }, [currentList, listVendors]);

  if (!currentList && !listLookupPending) {
    return (
      <Navigate to={`${MY_VENDORS_ROUTE}?tab=${MY_VENDORS_TAB_LISTS}`} replace />
    );
  }

  if (!currentList) {
    return (
      <div className="min-h-screen bg-gray-50 px-4 py-8 sm:px-6 lg:px-8">
        <p className="text-sm text-gray-500">Loading vendor list…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full min-w-0 bg-gray-50 px-4 pb-6 sm:px-6 lg:px-8">
      <div className="w-full min-w-0 py-8">
        <nav className="mb-4 flex" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <button
                type="button"
                onClick={() => navigate("/Providers")}
                className="text-sm text-gray-500 transition-colors hover:text-indigo-600"
              >
                Search Vendors
              </button>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="mx-1 h-3 w-3 text-gray-400"
                  aria-hidden="true"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <button
                  type="button"
                  onClick={() => navigate(`${MY_VENDORS_ROUTE}?tab=${MY_VENDORS_TAB_LISTS}`)}
                  className="text-sm text-gray-500 transition-colors hover:text-indigo-600"
                >
                  My Vendors
                </button>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <svg
                  className="mx-1 h-3 w-3 text-gray-400"
                  aria-hidden="true"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-sm font-medium text-gray-900">{currentList.name}</span>
              </div>
            </li>
          </ol>
        </nav>

        <div className="mb-6">
          <div className="flex items-start justify-between gap-4">
          {isRenaming && !isSystemList ? (
            <div className={`mr-4 flex-1 ${LIST_INLINE_FIELD_WRAPPER_CLASS}`}>
              <input
                ref={nameInputRef}
                type="text"
                value={editedName}
                onChange={(event) => setEditedName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") handleSaveRename();
                  if (event.key === "Escape") setIsRenaming(false);
                }}
                onBlur={handleSaveRename}
                className={`${LIST_INLINE_FIELD_CONTROL_CLASS} py-1.5 text-2xl font-semibold text-gray-900 placeholder:text-gray-400 sm:leading-6`}
              />
            </div>
          ) : (
            <h1 className="text-2xl font-semibold text-gray-900">{currentList.name}</h1>
          )}

          <Menu as="div" className="relative inline-block text-left">
            <MenuButton className="flex items-center rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
              <EllipsisVerticalIcon className="h-6 w-6" aria-hidden="true" />
            </MenuButton>
            <MenuItems className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    onClick={handleExportCsv}
                    className={`${focus ? "bg-gray-100" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                  >
                    Export CSV
                  </button>
                )}
              </MenuItem>
              <MenuItem>
                {({ focus }) => (
                  <button
                    type="button"
                    disabled={isExportingExcel}
                    onClick={handleExportExcel}
                    className={`${focus ? "bg-gray-100" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                  >
                    {isExportingExcel ? "Exporting…" : "Export Excel"}
                  </button>
                )}
              </MenuItem>
              {!isSystemList ? (
                <>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={() => {
                          setEditedName(currentList.name);
                          setIsRenaming(true);
                          setTimeout(() => nameInputRef.current?.focus(), 0);
                        }}
                        className={`${focus ? "bg-gray-100" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                      >
                        Rename List
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={handleDuplicateList}
                        className={`${focus ? "bg-gray-100" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                      >
                        Duplicate List
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        type="button"
                        onClick={() => setShowDeleteModal(true)}
                        className={`${focus ? "bg-red-50" : ""} flex w-full items-center px-4 py-2 text-sm text-red-600`}
                      >
                        Delete List
                      </button>
                    )}
                  </MenuItem>
                </>
              ) : (
                <MenuItem>
                  {({ focus }) => (
                    <button
                      type="button"
                      onClick={handleDuplicateList}
                      className={`${focus ? "bg-gray-100" : ""} flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                    >
                      Duplicate List
                    </button>
                  )}
                </MenuItem>
              )}
            </MenuItems>
          </Menu>
          </div>

          <p className="mt-1 text-sm text-gray-500">
            {currentList.createdAt
              ? `${new Date(currentList.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })} • `
              : ""}
            {listVendors.length} {listVendors.length === 1 ? "vendor" : "vendors"}
          </p>

          {!isSystemList ? (
            <div className="relative mt-4">
              <div className={LIST_NOTES_FIELD_WRAPPER_CLASS}>
                <textarea
                  rows={1}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  onFocus={() => setIsEditingNotes(true)}
                  placeholder="Add notes about this vendor list..."
                  className={`${LIST_INLINE_FIELD_CONTROL_CLASS} resize-none overflow-hidden !bg-transparent px-3 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 sm:leading-6`}
                  onInput={(event) => {
                    event.target.style.height = "auto";
                    event.target.style.height = `${event.target.scrollHeight}px`;
                  }}
                />
              </div>
              {isEditingNotes ? (
                <div className="mt-2 flex items-center justify-end gap-1">
                  <button
                    type="button"
                    onClick={handleSaveNotes}
                    className="rounded-md bg-green-100 p-1 text-green-600 transition-colors hover:bg-green-200"
                    title="Save"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNotes(currentList.notes || "");
                      setIsEditingNotes(false);
                    }}
                    className="rounded-md bg-red-100 p-1 text-red-600 transition-colors hover:bg-red-200"
                    title="Cancel"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              ) : null}
            </div>
          ) : currentList.notes ? (
            <p className="mt-4 text-sm text-gray-600">{currentList.notes}</p>
          ) : null}
        </div>

        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-sm:w-full sm:flex-1">
              <ToolbarSearch
                value={searchQuery}
                onDebouncedChange={(text) =>
                  dispatch(setSearchTextByPage({ pageKey: PAGE_KEY, searchText: text }))
                }
                placeholder="Search vendor list"
                inputClassName="w-[300px] max-w-full"
                className="max-w-xl"
              />
            </div>
            <div className="flex items-center">
              <PageToolbarFilters
                sections={filterSections}
                selectedIds={selectedIds}
                setSelectedIds={setSelectedIds}
                getOptionValue={defaultGetOptionValue}
              />
              <ManualSortToggle enabled={isManualSort} onToggle={handleToggleManualSort} />
            </div>
          </div>
        </div>

        <MyVendorsSelectionBar
          selectedVendors={selectedVendors}
          allSelected={allFilteredSelected}
          onSelectAll={handleSelectAllFiltered}
          onRemoveVendor={(vendor) =>
            setSelectedVendorIds((prev) => {
              const next = new Set(prev);
              next.delete(String(vendor.id));
              return next;
            })
          }
          onCreateList={() => setCreateListModalOpen(true)}
          onAddToList={() => handleOpenAddToListModal(selectedVendors)}
          onCompare={handleCompareSelected}
          showCompare={canCompareTrialExperience}
          showRemoveSelected
          onRemoveSelected={handleRequestRemoveSelected}
        />

        <div className="w-full min-w-0">
          <VendorListViewGrid
            vendors={filteredVendors}
            sponsorId={sponsorId}
            isManualSort={isManualSort}
            manualSortOrder={manualSortOrder}
            defaultSortOrder={defaultSortOrder}
            onReorderVendors={handleReorderVendors}
            onColumnSort={() => setIsManualSort(false)}
            selectedVendorIds={selectedVendorIds}
            onToggleVendorSelection={handleToggleVendorSelection}
            allFilteredSelected={allFilteredSelected}
            someFilteredSelected={someFilteredSelected}
            onSelectAllFiltered={handleSelectAllFiltered}
            onVendorAction={handleVendorAction}
            onRequestAction={handleRequestAction}
            onRemoveFromList={handleRequestRemoveFromList}
            canRemoveFromList={canRemoveFromList}
            onAddToList={(vendor) => handleOpenAddToListModal([vendor])}
            showRemoveFromList
          isBookmarked={isBookmarked}
            isVendorActionPending={isVendorActionPending}
          />
        </div>
      </div>

      <RemoveVendorFromListModal
        isOpen={Boolean(vendorPendingRemove) || vendorsPendingBulkRemove.length > 0}
        vendorName={vendorPendingRemove?.name || ""}
        vendorCount={vendorPendingRemove ? 1 : vendorsPendingBulkRemove.length}
        skippedCount={bulkRemoveSkippedCount}
        onConfirm={handleConfirmRemoveFromList}
        onCancel={handleCancelRemoveModal}
        isSubmitting={isRemovingVendor}
      />

      <DeleteVendorListModal
        isOpen={showDeleteModal}
        listName={currentList.name}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteModal(false)}
      />

      <CreateVendorListModal
        isOpen={createListModalOpen}
        onClose={() => setCreateListModalOpen(false)}
        onCreateList={handleCreateListFromSelection}
        existingListNames={userVendorLists.map((list) => list.name)}
        initialVendors={selectedVendors}
        isSubmitting={listMutating}
      />

      <AddToListModal
        isOpen={addToListModalOpen}
        onClose={() => {
          setAddToListModalOpen(false);
          setAddToListTargetVendors([]);
        }}
        selectedVendors={addToListTargetVendors}
        vendorLists={vendorLists}
        onAddToList={handleAddVendorsToList}
        isSubmitting={listMutating || membershipMutating}
      />

      <MyVendorsLibraryDrawer documentId={libraryDrawerDocId} />
    </div>
  );
}
